<template lang="pug">
  #member-settings-step-entries-form
    BNotification(:active.sync="error" type="is-danger" :duration="3000")
      | {{ errorMessage }}
    .mb-2
      WtTitle.mb-1(h3) Add or Edit Steps
      .sub-title Add or Edit steps for this member up to a month in the past.
    .mb-2.p-2.super-admin-only(v-if="$auth.user().isWtAdmin")
      .sub-title.has-text-red ONLY SuperAdmin can fetch steps for any date.
      BField(
        label="Start Date"
      )
        BDatepicker(
          placeholder="Click to select..."
          :date-formatter="(date) => this.date(date, 'DATE_FORMAT_DB').formatted"
          v-model="startDate"
        )
      BField(
        label="End Date"
      )
        BDatepicker(
          placeholder="Click to select..."
          :date-formatter="(date) => this.date(date, 'DATE_FORMAT_DB').formatted"
          v-model="endDate"
        )
      BField(
        label="Fetch Steps"
      )
        WtButton(@click.native="fetchStepData(startDate, endDate)") Fetch Steps

    template(v-if="stepDataFetched")
      .box.bordered
        BField(
          v-for="data in stepDataWithEmptyDays" :key="data.date"
          :label="date(data.date, 'DATE_FORMAT_FULL_MONTH').formatted"
          horizontal
        )
          BInput(
            placeholder="steps"
            v-model="data.steps"
            @blur="saveEntries(data)"
            @keyup.native.enter="saveEntries(data)"
            :iconRight="data.locked"
            :loading="saveInProgress === data.date"
          )
</template>

<script>
import { mapActions } from "vuex";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";
import { numberUtilityMixin } from "@/mixins/numberUtilityMixin";
import WtTitle from "@@/WtTitle/WtTitle";
import WtButton from "@@/WtButton/WtButton";

export default {
  name: "MemberSettingsEditStepEntriesForm",

  components: { WtTitle, WtButton },

  mixins: [dateUtilityMixin, numberUtilityMixin],

  props: {
    member: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      error: null,
      errorMessage: null,
      startDate: null,
      endDate: null,
      startDateFetched: null,
      endDateFetched: null,
      fetchInProgress: false,
      stepDataFetched: false,
      saveInProgress: false,
      stepData: []
    };
  },

  computed: {
    stepDataWithEmptyDays() {
      let stepDataWithEmptyDays = [];

      for (
        let m = this.moment(this.endDateFetched);
        m.isSameOrAfter(this.startDateFetched);
        m.subtract(1, "days")
      ) {
        let stepDataExistsForDay = this.stepData.find(data => {
          return this.moment(data.created).isSame(m, "day");
        });

        if (stepDataExistsForDay) {
          stepDataWithEmptyDays.push({
            id: stepDataExistsForDay.id,
            date: stepDataExistsForDay.created,
            steps: stepDataExistsForDay.steps,
            locked: stepDataExistsForDay.locked === 1 ? "lock" : ""
          });
        } else {
          stepDataWithEmptyDays.push({
            id: null,
            date: m.format("YYYY-MM-DD"),
            steps: 0,
            locked: ""
          });
        }
      }

      return stepDataWithEmptyDays;
    }
  },

  async mounted() {
    let endDate = this.moment();
    let startDate = this.moment().subtract(30, "days");
    if (this.moment(this.member.registeredDate).isSameOrAfter(startDate)) {
      startDate = this.moment(this.member.registeredDate);
    }

    await this.fetchStepData(startDate, endDate);
  },

  methods: {
    ...mapActions("members", ["fetchMemberStepData", "updateMemberStepData"]),
    ...mapActions(["snackBar"]),

    async fetchStepData(startDate, endDate) {
      this.stepDataFetched = false;
      this.fetchInProgress = true;

      this.startDateFetched = startDate;
      this.endDateFetched = endDate;

      const stepDataResponse = await this.fetchMemberStepData({
        startDate: this.formatDateForRequest(startDate),
        endDate: this.formatDateForRequest(endDate),
        memberId: this.member.id,
        perPage: this.daysBetweenInclusive(startDate, endDate)
      });

      this.stepData = stepDataResponse.data.data;
      this.stepDataFetched = true;
      this.fetchInProgress = false;
      this.startDateFetched = startDate;
      this.endDateFetched = endDate;
    },

    validateSteps(data) {
      if (data.steps <= 0 || !Number.isInteger(parseInt(data.steps))) {
        throw new Error("Steps must be a positive integer greater than 0.");
      }
    },

    async saveEntries(data) {
      this.saveInProgress = data.date;

      try {
        this.validateSteps(data);
        await this.updateMemberStepData({
          stepData: [data],
          memberId: this.member.id
        });

        data.locked = "lock";
      } catch (error) {
        if (error?.error) {
          this.$scrollTo("#member-settings-step-entries-form", 500, {
            offset: -70
          });
          this.error = true;
          this.errorMessage = error.error.message;
        } else {
          this.$scrollTo("#member-settings-step-entries-form", 500, {
            offset: -70
          });
          this.error = true;
          this.errorMessage = error.message;
        }
      }

      this.saveInProgress = false;
    }
  }
};
</script>
<style lang="sass" scoped>
.super-admin-only
  border: 1px solid red
</style>
